import { useEffect, useState } from "react";
import { ExternalLink, MessageSquarePlus, Sparkles } from "lucide-react";
import { HandoffLink, useHandoff, usePermission } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { AgentRunPanel, AssistantPanel, useCaseThread, useDeepLinks, useResolveDeepLinks, type DeepLinks } from "@ncp/assistant-sdk";
import { Button, EmptyState, Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, Skeleton, Tooltip, notifySaveFailed, saveFailureText } from "@ncp/ui";

/** This app's key in the solution registry: threads opened here are filed under it and reused on the next visit. */
const SURFACE = "backoffice";

const aiWorkspaceUrl = (links: DeepLinks) => links.links.find((l) => l.solutionKey === "assistant")?.url ?? null;

/**
 * The officer's conversation about this case with the department assistant, beside the case. It is the same thread the AI Workspace
 * lists, so a question started here continues there and the other way round. Mount with `key={caseId}`.
 */
export function CaseAssistant({ caseId, caseNumber, onOpenDocument }: { caseId: string; caseNumber: string; onOpenDocument: (documentId: string) => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { can } = usePermission();
  const handoff = useHandoff();
  const resolve = useResolveDeepLinks();
  const [open, setOpen] = useState(false);
  const [runId, setRunId] = useState<string | null>(null);
  const thread = useCaseThread(SURFACE);
  const threadId = thread.data?.id;
  const links = useDeepLinks(threadId ? { scope: "thread", threadId } : null);
  const continueUrl = links.data ? aiWorkspaceUrl(links.data) : null;
  // Links produced by confirmed actions (`/matters/{id}`) are relative to the AI Workspace.
  const workspaceOrigin = continueUrl ? new URL(continueUrl).origin : null;

  const start = (fresh: boolean) =>
    thread.mutate({ caseId, locale, fresh }, { onError: (e) => notifySaveFailed(e, t("backoffice:caseAssistant.failed")) });
  useEffect(() => {
    if (open && !thread.data && !thread.isPending && !thread.isError) start(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!can("assistant.use")) return null;

  const openCase = (id: string) => {
    if (id === caseId) { setOpen(false); return; }
    handoff.open(resolve({ scope: "case", caseId: id }).then((d) => (d.links.find((l) => l.solutionKey === SURFACE) ?? d.links[0])?.url ?? null, () => null));
  };
  const openArtifact = (artifactId: string) => handoff.open(resolve({ scope: "artifact", artifactId }).then(aiWorkspaceUrl, () => null));

  return (
    <>
      <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setOpen(true)}><Sparkles /> {t("backoffice:caseAssistant.button")}</Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="end" width="max-w-2xl" className="gap-0">
          <SheetHeader className="border-b border-sand-200 pb-4 pe-14">
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <SheetTitle>{t("backoffice:caseAssistant.title", { caseNumber })}</SheetTitle>
                <SheetDescription>{t("backoffice:caseAssistant.description")}</SheetDescription>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Tooltip content={t("backoffice:caseAssistant.newConversation")}>
                  <Button size="icon-sm" variant="ghost" aria-label={t("backoffice:caseAssistant.newConversation")} disabled={thread.isPending} onClick={() => start(true)}><MessageSquarePlus /></Button>
                </Tooltip>
                {continueUrl ? (
                  <Button size="sm" variant="outline" asChild>
                    <HandoffLink href={continueUrl}><ExternalLink /> {t("backoffice:caseAssistant.openInWorkspace")}</HandoffLink>
                  </Button>
                ) : null}
              </div>
            </div>
          </SheetHeader>
          <div className="flex min-h-0 flex-1 flex-col">
            {threadId ? (
              <AssistantPanel
                key={threadId}
                threadId={threadId}
                compact
                className="flex-1 rounded-none border-0 shadow-none"
                onOpenDocument={(documentId) => { onOpenDocument(documentId); setOpen(false); }}
                onOpenArtifact={openArtifact}
                onOpenCase={openCase}
                onOpenRun={setRunId}
                onOpenLink={workspaceOrigin ? (link) => handoff.open(`${workspaceOrigin}${link}`) : undefined}
              />
            ) : thread.isError ? (
              <div className="flex flex-1 items-center justify-center p-6">
                <EmptyState icon={Sparkles} title={t("backoffice:caseAssistant.failed")} description={saveFailureText(thread.error)} action={<Button variant="outline" onClick={() => start(false)}>{t("actions.retry")}</Button>} />
              </div>
            ) : (
              <div className="space-y-3 p-6">
                <p className="text-xs text-muted-foreground">{t("backoffice:caseAssistant.starting")}</p>
                <Skeleton className="h-16 w-3/4" />
                <Skeleton className="ms-auto h-10 w-1/2" />
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
      <Sheet open={!!runId} onOpenChange={(o) => !o && setRunId(null)}>
        <SheetContent side="end" width="max-w-xl">
          {runId ? <AgentRunPanel runId={runId} onClose={() => setRunId(null)} /> : null}
        </SheetContent>
      </Sheet>
    </>
  );
}
